import { useState, useMemo } from "react";
import { X } from "lucide-react";
import { buildSizeEntries } from "./ordersUtils";
import { num, calcReturnMetrics } from "../../utils/calculations";

const RETURN_REASONS = [
  "Defective / Damaged Product",
  "Weight Mismatch",
  "Quality / Purity Issue",
  "Wrong Item Delivered",
  "Design Mismatch",
  "Customer Cancellation",
  "Other",
];

/**
 * ReturnModal
 *
 * For each order → each size:
 *   - returnReason: dropdown option per card / order
 *   - returnQty: -/+ stepper switch (0 <= returnQty <= maxQty)
 *   - returnGrossWt: auto-filled (returnQty * grossWT), editable by input
 *   - returnNetWt: auto-filled (returnQty * netwt), editable by input
 *   - finalQty: maxQty - returnQty
 *   - finalGrossWt: qty*grossWT - returnGrossWt
 *   - finalNetWt: qty*netwt - returnNetWt
 */
function ReturnModal({ group, groupKey, onReturn, onClose }) {

  // returnMap: { [orderId]: { productName, imageUrl, returnReason, sizes[] } }
const [returnMap, setReturnMap] = useState(() => {
  const map = {};
  group.orders.forEach((order) => {
    const sizeEntries = buildSizeEntries(order, num);
    map[order.id] = {
      productName: order.productName || "Unnamed Product",
      imageUrl: order.images?.[0]?.url?.trim() || "",
      returnReason: order.returnReason || sizeEntries[0]?.returnReason || "",
      sizes: sizeEntries.map((sd) => ({
        size: sd.size,
        maxQty: sd.maxQty,
        pieceGrossWt: sd.grossWT,   // gross wt per piece/unit (from orderDetail)
        pieceNetWt: sd.netwt,       // net wt per piece/unit   (from orderDetail)
        stampCertificateQtyPerSet: sd.stampCertificateQtyPerSet,
        // ── return state ──
        returnQty: sd.returnQty || 0,
        returnGrossWt: sd.returnGrossWt || 0,
        returnNetWt: sd.returnNetWt || 0,
      })),
    };
  });
  return map;
});

  // ── Reason change per order ────────────────────────────────────────────────
  const handleReasonChange = (orderId, value) => {
    setReturnMap((prev) => ({
      ...prev,
      [orderId]: { ...prev[orderId], returnReason: value },
    }));
  };

  // ── Stepper change (- / + switch) ──────────────────────────────────────────
  const changeQty = (orderId, sizeIdx, delta) => {
    setReturnMap((prev) => {
      const order = { ...prev[orderId] };
      const sizes = [...order.sizes];
      const sd = { ...sizes[sizeIdx] };

      const currentQty = num(sd.returnQty);
      const newQty = Math.max(0, Math.min(sd.maxQty, currentQty + delta));

      sd.returnQty = newQty;
      sd.returnGrossWt = parseFloat((newQty * sd.pieceGrossWt).toFixed(3));
      sd.returnNetWt = parseFloat((newQty * sd.pieceNetWt).toFixed(3));

      sizes[sizeIdx] = sd;
      return { ...prev, [orderId]: { ...order, sizes } };
    });
  };

  // ── Gross Wt Input Change ───────────────────────────────────────────────────
  const handleGrossWtChange = (orderId, sizeIdx, rawValue) => {
    setReturnMap((prev) => {
      const order = { ...prev[orderId] };
      const sizes = [...order.sizes];
      sizes[sizeIdx] = { ...sizes[sizeIdx], returnGrossWt: rawValue };
      return { ...prev, [orderId]: { ...order, sizes } };
    });
  };

  // ── Net Wt Input Change ────────────────────────────────────────────────────
  const handleNetWtChange = (orderId, sizeIdx, rawValue) => {
    setReturnMap((prev) => {
      const order = { ...prev[orderId] };
      const sizes = [...order.sizes];
      sizes[sizeIdx] = { ...sizes[sizeIdx], returnNetWt: rawValue };
      return { ...prev, [orderId]: { ...order, sizes } };
    });
  };

  // ── Derived Grand Totals ───────────────────────────────────────────────────
const { totalReturnQty, totalReturnGrossWt, totalReturnNetWt } = useMemo(() => {
  let qty = 0, gross = 0, net = 0;
  Object.values(returnMap).forEach((item) => {
    item.sizes.forEach((sd) => {
      qty += num(sd.returnQty);
      gross += num(sd.returnGrossWt);
      net += num(sd.returnNetWt);
    });
  });
  return {
    totalReturnQty: qty,
    totalReturnGrossWt: parseFloat(gross.toFixed(3)),
    totalReturnNetWt: parseFloat(net.toFixed(3)),
  };
}, [returnMap]);

  const hasSelection = totalReturnQty > 0;

  // ── Confirm Return ─────────────────────────────────────────────────────────
  function handleReturn() {
    if (!hasSelection) return;

    const returnItems = Object.entries(returnMap)
      .filter(([, item]) => item.sizes.some((sd) => num(sd.returnQty) > 0))
      .map(([orderId, item]) => {
        const order = group.orders.find((o) => o.id === orderId);

        const updatedOrderDetail = {};
        const sizeList = [];

        item.sizes.forEach((sd) => {
          const returnQty = num(sd.returnQty);
          const returnGrossWt = num(sd.returnGrossWt);
          const returnNetWt = num(sd.returnNetWt);

          const finalQty = sd.maxQty - returnQty;
          const totalInitialGross = sd.maxQty * sd.pieceGrossWt;
          const totalInitialNet = sd.maxQty * sd.pieceNetWt;

          const finalGrossWt = parseFloat(Math.max(0, totalInitialGross - returnGrossWt).toFixed(3));
          const finalNetWt = parseFloat(Math.max(0, totalInitialNet - returnNetWt).toFixed(3));

          const { fineWeight, totalMc } = calcReturnMetrics(order, sd.pieceNetWt);

          // Update each size in orderDetail object
          updatedOrderDetail[sd.size] = {
            grossWT: sd.pieceGrossWt,
            qty: sd.maxQty,
            netwt: sd.pieceNetWt,
            stampCertificateQtyPerSet: sd.stampCertificateQtyPerSet,
            returnReason: item.returnReason || "",
            returnQty,
            returnGrossWt,
            returnNetWt,
            finalQty,
            finalGrossWt,
            finalNetWt,
          };

          sizeList.push({
            size: sd.size,
            returnReason: item.returnReason || "",
            returnQty,
            returnGrossWt,
            returnNetWt,
            finalQty,
            finalGrossWt,
            finalNetWt,
            fineWeight: parseFloat((fineWeight * returnQty).toFixed(3)),
            totalMc: parseFloat((totalMc * returnQty).toFixed(2)),
          });
        });

        const totalFineWt = sizeList.reduce((s, r) => s + r.fineWeight, 0);
        const totalMcSum = sizeList.reduce((s, r) => s + r.totalMc, 0);
        const totalQty = sizeList.reduce((s, r) => s + r.returnQty, 0);

        return {
          orderId,
          productName: item.productName,
          returnReason: item.returnReason || "",
          orderDetail: updatedOrderDetail,
          sizes: sizeList,
          fineWeight: parseFloat(totalFineWt.toFixed(3)),
          totalMc: parseFloat(totalMcSum.toFixed(2)),
          totalQty,
        };
      });

    console.log("Return payload:", JSON.stringify(returnItems, null, 2));
    onReturn(groupKey, returnItems);
    onClose();
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="od-modal-overlay" onClick={onClose}>
      <div className="od-modal od-modal--sm" onClick={(e) => e.stopPropagation()}>

        <div className="od-modal-header">
          <span className="od-modal-title">Return Piece</span>
          <button className="od-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="od-modal-body">
          <div className="od-return-product-list">
            {group.orders.map((order) => {
              const item = returnMap[order.id];
              if (!item) return null;

              return (
                <div key={order.id} className="od-return-product-row">
                  {/* Thumbnail */}
                  <div className="od-return-product-thumb">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.productName}
                        className="od-return-product-img"
                        loading="lazy"
                      />
                    ) : (
                      <div className="od-return-product-img-placeholder" />
                    )}
                  </div>

                  <div className="od-return-product-info">
                    <span className="od-return-product-name">{item.productName}</span>

                    {/* Per-card / per-order Return Reason Dropdown */}
                    <div className="od-return-reason-row">
                      <label className="od-return-reason-label">
                        Reason for Return
                        <select
                          className="od-return-reason-select"
                          value={item.returnReason || ""}
                          onChange={(e) => handleReasonChange(order.id, e.target.value)}
                          required
                        >
                          <option value="">-- Select Reason --</option>
                          {RETURN_REASONS.map((reason) => (
                            <option key={reason} value={reason}>
                              {reason}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <div className="od-return-size-rows">
                      {item.sizes.map((sd, sizeIdx) => {
const returnQty = num(sd.returnQty);
const returnGrossWt = num(sd.returnGrossWt);
                          const returnNetWt = num(sd.returnNetWt);

                        const finalQty = sd.maxQty - returnQty;
                        const totalInitialGross = sd.maxQty * sd.pieceGrossWt;
                        const totalInitialNet = sd.maxQty * sd.pieceNetWt;

                        const finalGrossWt = parseFloat(Math.max(0, totalInitialGross - returnGrossWt).toFixed(3));
                        const finalNetWt = parseFloat(Math.max(0, totalInitialNet - returnNetWt).toFixed(3));

                        return (
                          <div key={sd.size} className="od-return-size-row">

                            {/* Size Header */}
                            <div className="od-return-size-header">
                              <span className="od-return-size-label">{sd.size}</span>
                              <span className="od-return-detail">
                                Ordered: {sd.maxQty} pcs &nbsp;·&nbsp;
                                {sd.pieceGrossWt.toFixed(3)}g gross &nbsp;·&nbsp;
                                {sd.pieceNetWt.toFixed(3)}g net
                              </span>
                            </div>

                            {/* ── Controls Row (Stepper + Weight Inputs) ── */}
                            <div className="od-return-controls-row">

                              {/* -/+ Stepper Switch for Return Qty */}
                              <div className="od-return-qty-container">
                                <span className="od-input-sublabel">Return Qty</span>
                                <div className="od-return-qty-controls">
                                  <button
                                    type="button"
                                    className="od-qty-btn"
                                    onClick={() => changeQty(order.id, sizeIdx, -1)}
                                    disabled={returnQty <= 0}
                                  >
                                    −
                                  </button>
                                  <span className="od-qty-value">{returnQty}</span>
                                  <button
                                    type="button"
                                    className="od-qty-btn"
                                    onClick={() => changeQty(order.id, sizeIdx, 1)}
                                    disabled={returnQty >= sd.maxQty}
                                  >
                                    +
                                  </button>
                                  <span className="od-qty-max">/ {sd.maxQty}</span>
                                </div>
                              </div>

                              {/* Inputs for Return Gross Wt & Net Wt */}
                              <div className="od-return-wt-inputs">
                                <label className="od-return-input-label">
                                  Return Gross Wt (g)
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.001"
                                    className="od-return-input"
                                    value={sd.returnGrossWt}
                                    onChange={(e) => handleGrossWtChange(order.id, sizeIdx, e.target.value)}
                                  />
                                </label>
                                <label className="od-return-input-label">
                                  Return Net Wt (g)
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.001"
                                    className="od-return-input"
                                    value={sd.returnNetWt}
                                    onChange={(e) => handleNetWtChange(order.id, sizeIdx, e.target.value)}
                                  />
                                </label>
                              </div>

                            </div>

                            {/* Final breakdown summary */}
                            {returnQty > 0 && (
                              <div className="od-return-breakdown">
                                <div className="od-return-breakdown-col od-return-breakdown-col--final">
                                  <span className="od-breakdown-heading">Final (Remaining)</span>
                                  <span className="od-breakdown-row">
                                    <span className="od-breakdown-label">Qty</span>
                                    <span className="od-breakdown-value">{finalQty}</span>
                                  </span>
                                  <span className="od-breakdown-row">
                                    <span className="od-breakdown-label">Gross Wt</span>
                                    <span className="od-breakdown-value">{finalGrossWt.toFixed(3)}g</span>
                                  </span>
                                  <span className="od-breakdown-row">
                                    <span className="od-breakdown-label">Net Wt</span>
                                    <span className="od-breakdown-value">{finalNetWt.toFixed(3)}g</span>
                                  </span>
                                </div>
                              </div>
                            )}

                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Grand Totals */}
          {hasSelection && (
            <div className="od-return-total-row">
              <span className="od-return-total-label">Total Return</span>
              <span className="od-return-total-value">{totalReturnQty} pcs</span>
              <span className="od-return-total-label" style={{ marginLeft: "16px" }}>Gross</span>
              <span className="od-return-total-value">{totalReturnGrossWt.toFixed(3)}g</span>
              <span className="od-return-total-label" style={{ marginLeft: "16px" }}>Net</span>
              <span className="od-return-total-value">{totalReturnNetWt.toFixed(3)}g</span>
            </div>
          )}
        </div>

        <div className="od-modal-footer">
          <button className="od-btn-cancel" onClick={onClose}>Cancel</button>
          <button
            className="od-btn-return"
            onClick={handleReturn}
            disabled={!hasSelection}
          >
            Confirm Return
          </button>
        </div>

      </div>
    </div>
  );
}

export default ReturnModal;
